import EnumDataSource from "utils/enum-data-source.util";

export const RECORDING_STATE_STOPPED = 1;
export const RECORDING_STATE_IN_PROGRESS = 2;
export const RECORDING_STATE_PAUSED = 3;
export const RECORDING_STATE_SAVING = 4;

export const AMBIENT_SCRIBE_STATUS_AWAITING_UPLOAD = 1;
export const AMBIENT_SCRIBE_STATUS_UPLOADING = 2;
export const AMBIENT_SCRIBE_STATUS_UPLOADED = 3;
export const AMBIENT_SCRIBE_STATUS_TRANSCRIBED = 4;
export const AMBIENT_SCRIBE_STATUS_UPLOAD_FAILED = 5;
export const AMBIENT_SCRIBE_STATUS_RECORDING = 0;

export const AMBIENT_SCRIBE_STATUS_PROCESSING = 6;
export const AMBIENT_SCRIBE_STATUS_COMPLETED = 7;
export const AMBIENT_SCRIBE_STATUS_FAILED = 8;
export const AMBIENT_SCRIBE_STATUS_RETRYING = 9;
export const AMBIENT_SCRIBE_STATUS_CANCELLED = 10;
export const AMBIENT_SCRIBE_STATUS_PAUSED = 11;

export const WIDGET_RETURN_LATER_BUTTON_TOOLTIP =
  "Save this recording and return later. Open it from the scribe list to resume recording or complete when you are ready.";

export const AMBIENT_SCRIBE_STATUSES = new EnumDataSource({
  [AMBIENT_SCRIBE_STATUS_RECORDING]: "Recording",
  [AMBIENT_SCRIBE_STATUS_AWAITING_UPLOAD]: "Awaiting Upload",
  [AMBIENT_SCRIBE_STATUS_UPLOADING]: "Uploading…",
  [AMBIENT_SCRIBE_STATUS_UPLOADED]: "Uploaded",
  [AMBIENT_SCRIBE_STATUS_TRANSCRIBED]: "Transcribed",
  [AMBIENT_SCRIBE_STATUS_UPLOAD_FAILED]: "Upload Failed",
  [AMBIENT_SCRIBE_STATUS_PROCESSING]: "Processing",
  [AMBIENT_SCRIBE_STATUS_COMPLETED]: "Completed",
  [AMBIENT_SCRIBE_STATUS_FAILED]: "Failed",
  [AMBIENT_SCRIBE_STATUS_RETRYING]: "Retrying",
  [AMBIENT_SCRIBE_STATUS_CANCELLED]: "Cancelled",
  [AMBIENT_SCRIBE_STATUS_PAUSED]: "Paused"
});

export const AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING = 0;
export const AMBIENT_SCRIBE_BACKEND_STATUS_UPLOADED = 1;
export const AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING = 2;
export const AMBIENT_SCRIBE_BACKEND_STATUS_TRANSCRIBED = 3;
export const AMBIENT_SCRIBE_BACKEND_STATUS_COMPLETED = 4;
export const AMBIENT_SCRIBE_BACKEND_STATUS_FAILED = 5;
export const AMBIENT_SCRIBE_BACKEND_STATUS_RETRYING = 6;
export const AMBIENT_SCRIBE_BACKEND_STATUS_CANCELLED = 7;
// Paused for later (SN-17727). Backend status code 8 — see docs/ambient-scribes/.
export const AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED = 8;

/**
 * Every backend status the Scribes list asks for. `PAUSED` only behind its flag: a backend that
 * predates it does not know the value.
 */
export function getScribeListStatuses(
  isPausedFeatureEnabled: boolean
): number[] {
  const status = [
    AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING,
    AMBIENT_SCRIBE_BACKEND_STATUS_UPLOADED,
    AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING,
    AMBIENT_SCRIBE_BACKEND_STATUS_TRANSCRIBED,
    AMBIENT_SCRIBE_BACKEND_STATUS_COMPLETED,
    AMBIENT_SCRIBE_BACKEND_STATUS_FAILED,
    AMBIENT_SCRIBE_BACKEND_STATUS_RETRYING,
    AMBIENT_SCRIBE_BACKEND_STATUS_CANCELLED
  ];
  if (isPausedFeatureEnabled) {
    status.push(AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED);
  }
  return status;
}

export const AMBIENT_SCRIBE_BACKEND_STATUSES = new EnumDataSource({
  [AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING]: "Recording",
  [AMBIENT_SCRIBE_BACKEND_STATUS_UPLOADED]: "Uploaded",
  [AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING]: "Processing",
  [AMBIENT_SCRIBE_BACKEND_STATUS_TRANSCRIBED]: "Transcribed",
  [AMBIENT_SCRIBE_BACKEND_STATUS_COMPLETED]: "Completed",
  [AMBIENT_SCRIBE_BACKEND_STATUS_FAILED]: "Failed",
  [AMBIENT_SCRIBE_BACKEND_STATUS_RETRYING]: "Retrying",
  [AMBIENT_SCRIBE_BACKEND_STATUS_CANCELLED]: "Cancelled",
  [AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED]: "Paused"
});

/** Scribe modal stack: base dialog, popovers/tooltips, nested confirms (above base). */
export const getScribeModalZIndex = (snackbarZ: number) => snackbarZ + 101;
export const getScribeModalPopoverZIndex = (snackbarZ: number) =>
  snackbarZ + 111;
export const getScribeModalNestedDialogZIndex = (snackbarZ: number) =>
  snackbarZ + 112;

// Tab sync action constants
export const TAB_SYNC_ACTIONS = {
  START_SCRIBE_RECORDING_ON_TAB: "START_SCRIBE_RECORDING_ON_TAB",
  RESUME_SCRIBE_RECORDING_ON_TAB: "RESUME_SCRIBE_RECORDING_ON_TAB",
  PAUSE_SCRIBE_RECORDING_ON_TAB: "PAUSE_SCRIBE_RECORDING_ON_TAB",
  STOP_SCRIBE_RECORDING_ON_TAB: "STOP_SCRIBE_RECORDING_ON_TAB",
  REQUEST_RECORDING_STATUS: "REQUEST_RECORDING_STATUS",
  RECORDING_STATUS_RESPONSE: "RECORDING_STATUS_RESPONSE",
  // "Are you still recording?" cross-tab sync (SN-19277). Unlike the notifications above, these
  // two are asymmetric: STATE is published by the recording-owning tab only and every other tab
  // mirrors it, while ACTION is a *command* a non-owning tab sends because it has no
  // MediaRecorder of its own to stop. See the prompt section in this feature's CLAUDE.md.
  STILL_RECORDING_PROMPT_STATE: "STILL_RECORDING_PROMPT_STATE",
  STILL_RECORDING_PROMPT_ACTION: "STILL_RECORDING_PROMPT_ACTION",
  // SN-19902: the recording tab's gap notice, {text | null}, mirrored as a snackbar elsewhere.
  RECORDING_INTERRUPTION_NOTICE: "RECORDING_INTERRUPTION_NOTICE"
} as const;

const RECORDING_LIFECYCLE_ACTIONS: readonly string[] = [
  TAB_SYNC_ACTIONS.START_SCRIBE_RECORDING_ON_TAB,
  TAB_SYNC_ACTIONS.RESUME_SCRIBE_RECORDING_ON_TAB,
  TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB,
  TAB_SYNC_ACTIONS.STOP_SCRIBE_RECORDING_ON_TAB
];

/**
 * Whether a tab-sync message announces that a recording started, paused, resumed or stopped. The
 * same channel carries status checks and other features' messages.
 */
export const isRecordingLifecycleAction = (action: string) =>
  RECORDING_LIFECYCLE_ACTIONS.includes(action);

// Database store names
export const DATABASE_STORES = {
  RECORDINGS: "recordings",
  RECORDINGS_CHUNKS: "recordings_chunks",
  RECORDING_SESSIONS: "recording_sessions"
} as const;

// Database indexes
export const DATABASE_INDEXES = {
  UPLOAD_STATUS: "uploadStatus"
} as const;

// MediaRecorder states
export const MEDIA_RECORDER_STATES = {
  INACTIVE: "inactive",
  RECORDING: "recording",
  PAUSED: "paused"
} as const;

// File types and MIME types
export const AUDIO_CONFIG = {
  MIME_TYPE: "audio/webm",
  FILE_EXTENSION: ".webm"
} as const;

// Favicon paths
export const FAVICON_PATHS = {
  DEFAULT: "/favicon.ico",
  RECORDING: "/favicon-red.ico",
  PAUSED: "/favicon-green.ico"
} as const;

// Recording session ID
export const RECORDING_SESSION_ID = "current";

// Error messages
export const ERROR_MESSAGES = {
  DATABASE_NOT_READY: "Database not ready, cannot start recording",
  UPLOAD_WORKER_NOT_AVAILABLE: "Upload worker not available",
  NO_RECORDING_SESSION_TO_STOP: "No recording session to stop",
  NO_VALID_RECORDING_SESSION_TO_RESUME: "No valid recording session to resume",
  RECORDING_STOPPED_MAX_DURATION:
    "Recording stopped – maximum duration reached",
  RECORDING_CONNECTION_FAILED:
    "Failed to connect to recording service. Please try again.",
  RECORDING_SERVICE_ERROR: "Recording service error. Please try resuming.",
  TRANSCRIPTION_SERVICE_ERROR:
    "Transcription service error occurred during recording.",
  // B6 (PZ review): the FE copy previously told the user to "try stopping again" while the
  // widget only offers Resume in this state — and an immediate repeat stop would 404 anyway,
  // since consumer state was cleared. Match the corrected backend contract: resume first (a
  // new segment reconnects with the existing scribeId), then stop to retry the save.
  TRANSCRIPT_PERSIST_FAILED:
    "The transcript could not be saved. Resume the recording, then stop again to retry saving it.",
  RECORDING_DEGRADED_WARNING:
    "A recording issue was detected — part of the transcript may be affected. You can keep recording.",
  MICROPHONE_LOST_PAUSED:
    "Your microphone was disconnected and could not be reopened. Recording is paused. Reconnect your microphone, then press Resume."
} as const;

// Warning messages — recording succeeded, but not the way the user configured it.
// Deliberately not in ERROR_MESSAGES: these fire on the success path.
export const WARNING_MESSAGES = {
  MICROPHONE_FELL_BACK_TO_DEFAULT:
    "Your selected microphone isn't available. Recording started with your computer's default microphone instead.",
  MICROPHONE_INTERRUPTED_RESUMED:
    "Your microphone was interrupted. Recording has resumed on the same microphone.",
  MICROPHONE_INTERRUPTED_SWITCHED:
    "Your microphone was interrupted and is no longer available. Recording has resumed on your computer's default microphone."
} as const;

// Snackbar copy for the softphone's auto-decline while a Scribe recording is live (ENG-2001).
export const CALL_DECLINED_WHILE_RECORDING_MESSAGE = (caller: string) =>
  `Call from ${caller} declined while recording`;

// Success messages
export const SUCCESS_MESSAGES = {
  SCRIBE_RECORD_UPLOADED: "Scribe record uploaded",
  SCRIBE_RECORD_UPLOAD_FAILED: "Scribe record upload failed"
} as const;

// Upload messages
export const UPLOAD_MESSAGES = {
  UPLOADING_AUDIO_SCRIBE_DATA: "Uploading audio scribe data…"
} as const;
